import React, { useState } from 'react';
import RateOption from './RateOption';
import { useBooking } from '../context/BookingContext';

const RoomCard = ({ room, onImageClick }) => {
  const { state } = useBooking();
  // vansh - mobile state for expanding room details
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  const getCartQuantity = (rateId) => {
    // Find item for this specific rate
    const item = state.cart.find(item => item.id === rateId && item.roomId === room.id);
    return item ? item.quantity : 0;
  };

  // Use total_rooms from the API response instead of available_rooms count
  const totalRoomsCount = room.total_rooms || 0;

  // vansh - handler for mobile expand/collapse
  const handleMobileToggle = () => {
    setIsMobileExpanded(!isMobileExpanded);
  };

  // Get terms and conditions from the room data (category level)
  const getTermsAndConditions = () => {
    // Check if room has terms_and_conditions array with items
    if (room.terms_and_conditions && 
        Array.isArray(room.terms_and_conditions) && 
        room.terms_and_conditions.length > 0) {
      
      // Filter out empty strings and return the array
      return room.terms_and_conditions.filter(term => 
        term && term.trim() !== ''
      );
    }
    
    return [];
  };

  const termsAndConditions = getTermsAndConditions();

  return (
    // vansh - desktop view with original styling (unchanged)
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-md">
      {/* vansh - desktop layout - hidden on mobile */}
      <div className="hidden md:flex flex-col md:flex-row">
        {/* Room Image */}
        <div className="md:w-4/12 relative group cursor-pointer">
          <img
            src={room.image}
            alt={room.name}
            className="w-full h-48 object-cover"
            onClick={() => onImageClick(room)}
          />
          <div 
            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center"
            onClick={() => onImageClick(room)}
          >
            <button className="expand-icon-btn p-2 bg-white/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-90 group-hover:scale-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m4.5 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Room Details */}
        <div className="md:w-8/12 p-4 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-start mb-1">
              <h2 className="text-xl font-bold text-text-primary tracking-tight">{room.name}</h2>
              {totalRoomsCount > 0 && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                  {totalRoomsCount} room{totalRoomsCount !== 1 ? 's' : ''} available
                </span>
              )}
            </div>
            <p className="text-xs text-text-secondary mb-2 flex items-center space-x-3">
              <span>{room.size}</span>
              <span className="text-gray-300">•</span>
              <span>{room.guests}</span>
            </p>
            <p className="text-text-secondary text-sm leading-relaxed mt-2 mb-3 line-clamp-2">
      {room.description}
    </p>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {room.amenities.slice(0, 4).map((amenity, index) => (
              <span key={index} className="text-xs font-medium text-text-secondary bg-slate-100 rounded px-2 py-0.5">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 4 && (
              <span  onClick={() => onImageClick(room)} className="text-xs font-medium text-text-secondary bg-slate-100 rounded px-2 py-0.5 cursor-pointer">
                +{room.amenities.length - 4} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rate Options - Desktop */}
      <div className="hidden md:block bg-white px-4 py-4 border-t border-gray-200">
        {room.rates && room.rates.length > 0 ? (
          <>
            {/* Use discount name instead of "Best Available Rate" */}
            <h3 className="text-base font-semibold text-text-primary mb-2">
              {room.rates[0].offer?.discount_name || 'Best Available Rate '}
            </h3>
            <div className="space-y-0 divide-y divide-gray-200 pl-4">
              {room.rates.map(rate => (
                <RateOption
                  key={rate.id}
                  rate={rate}
                  room={room}
                  currentQuantity={getCartQuantity(rate.id)}
                />
              ))}
            </div>

            {/* Dynamic Terms and Conditions - Desktop */}
            {termsAndConditions.length > 0 && (
              <div className="text-xs space-y-1 pt-1.5 border-t border-gray-200 mt-1">
                {termsAndConditions.map((term, index) => (
                  <p key={index}>
                    <span className="text-xs font-medium text-gray-700 flex items-center mt-0.5">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      {term}
                    </span>
                  </p>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-text-secondary text-sm">No rate plans available for the selected dates.</p>
        )}
      </div>

      {/* vansh - MOBILE LAYOUT START - App-like card design for mobile only */}
      <div className="md:hidden flex flex-col bg-white">
        {/* vansh - Mobile Hero Image with gradient overlay */}
        <div 
          className="relative h-56 w-full overflow-hidden cursor-pointer group"
          onClick={() => {
            onImageClick(room);
            setIsMobileExpanded(true);
          }}
        >
          <img
            src={room.image}
            alt={room.name}
            className="w-full h-full object-cover"
          />
          {/* vansh - gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          
          {/* vansh - mobile expand button overlay */}
          <div className="absolute top-3 right-3">
            <button 
              className="p-2.5 bg-white/90 rounded-full shadow-lg active:scale-95 transition-transform"
              onClick={(e) => {
                e.stopPropagation();
                onImageClick(room);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-900">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m4.5 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              </svg>
            </button>
          </div>

          {/* vansh - availability badge on image */}
          {totalRoomsCount > 0 && (
            <div className="absolute bottom-3 left-3">
              <span className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-full font-semibold shadow-lg">
                {totalRoomsCount} available
              </span>
            </div>
          )}
        </div>

        {/* vansh - Room Info Section */}
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-lg font-bold text-text-primary mb-1 truncate">{room.name}</h2>
          
          {/* vansh - Room specs in compact row */}
          <div className="flex gap-3 text-xs text-text-secondary mb-2">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5"></path>
              </svg>
              <span>{room.size}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 10a2 2 0 100-4 2 2 0 000 4zM.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"></path>
              </svg>
              <span>{room.guests}</span>
            </div>
          </div>

          {/* vansh - Quick description preview */}
          <p className="text-sm text-text-secondary line-clamp-2 mb-3">{room.description}</p>

          {/* vansh - Amenities showcase */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {room.amenities.slice(0, 3).map((amenity, index) => (
              <span key={index} className="text-xs font-medium text-text-secondary bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 3 && (
              <span className="text-xs font-medium text-text-secondary bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                +{room.amenities.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* vansh - Dynamic Terms and Conditions Pills - Mobile - Only show if terms exist */}
        {termsAndConditions.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {termsAndConditions.map((term, index) => (
              <div key={index} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-full font-medium">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span className="truncate max-w-[120px]">{term}</span>
              </div>
            ))}
          </div>
        )}

        {/* vansh - Expandable Rates Section - Initially Collapsed */}
        <div className="border-t border-gray-200">
          <button
            onClick={handleMobileToggle}
            className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <span className="font-semibold text-text-primary text-sm">
              {room.rates && room.rates.length > 0 
                ? `${room.rates[0].offer?.discount_name || 'Best Available Rate'}`
                : 'Rate Options'
              }
            </span>
            {/* vansh - animated chevron icon */}
            <svg 
              className={`w-5 h-5 text-text-secondary transition-transform duration-300 ${isMobileExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>

          {/* vansh - Rates Expanded Section - Smooth Collapse/Expand Animation */}
          {isMobileExpanded && (
            <div className="max-h-max overflow-hidden bg-white animate-in fade-in duration-200">
              <div className="px-4 py-3 space-y-0 divide-y divide-gray-150">
                {room.rates && room.rates.length > 0 ? (
                  <>
                    {room.rates.map(rate => (
                      <RateOption
                        key={rate.id}
                        rate={rate}
                        room={room}
                        currentQuantity={getCartQuantity(rate.id)}
                      />
                    ))}
                  </>
                ) : (
                  <p className="text-text-secondary text-sm py-3">No rate plans available for the selected dates.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* vansh - MOBILE LAYOUT END */}
    </div>
  );
};

export default RoomCard;